import {
  MAX_PANEL,
  DIRECTION_OPTIONS,
  rowLabel,
  addAgentToPanel,
  reconcileTeamAdd,
  filterSelectableAgents,
  filterMatchableTeams,
  topSuggestion,
  buildRoundtableQaIn,
  scopeCaption,
  buildScopeLabel,
} from "./RoundtableView.helpers";
// Wave 5 · T1 — Roundtable: assemble a panel of agents (cap 10), scope
// their calls with the same date-range / direction / transcript filters
// used elsewhere, then ask free-text questions answered per agent and
// rolled up into a comparison takeaway (POST /dialpad/roundtable/qa).
//
// Everything here is session-only — there is no Save / persistence. The
// page state (panel, filters, Q&A blocks) lives entirely in React state
// and resets on reload or Clear Results.
//
// Scope-change semantics (owner decision): filters changed mid-thread
// apply to SUBSEQUENT questions only. Earlier blocks keep the answers,
// citations and scope they were asked under — each block snapshots its
// own ``call_refs`` refMap (pool-scoped per block, see searchQaCopy.ts
// for the invariant) and its own scope caption.
//
// Stage 3 of 3: the Copy Report button exports the ACTIVE tab as rich
// text via roundtableCopy.ts + the shared writeRichClipboard helper —
// the answer-presentation helpers (error copy, captions, matrix
// columns) live there too, shared with this render so the pasted
// report can never drift from the screen.
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  Copy,
  Loader2,
  MessagesSquare,
  Plus,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";

import { buildCallRefMap, linkifyCallIds, type CallRefMap } from "../lib/linkifyCallIds";
import {
  api,
  type AgentRow,
  type ApiError,
  type CallRef,
  type MyTeam,
  type RangeMode,
  type RoundtableAgentAnswer,
  type RoundtableDirection,
  type SuggestedQuestionsOut,
  type TimeRange,
} from "../lib/api";
import { TimeRangeControls } from "../components/TimeRangeControls";

import { useAuth } from "../auth.helpers";

import { useTimeRange } from "../lib/useTimeRange";
import { writeRichClipboard } from "../lib/richClipboard";
import { colorStyle, groupByColor } from "../lib/questionColors";
import {
  agentErrorCopy,
  answerCaption,
  buildRoundtableCopyPayload,
  matrixColumns,
} from "./roundtableCopy";

// PET-659 — default scope is the trailing 7-day rolling window, capped
// at the 100 most recent calls per agent (whichever limit is reached
// first). This is expressed through the SHARED TimeRangeControls (RAID
// #41 — one period control everywhere, never a forked range control):
// the "Weekly" preset in "Rolling" mode IS the trailing-7-day window
// ("Last 7 days"). A bounded / custom selection overrides the default
// and BOUNDS each agent's call pool server-side — it never tops up from
// outside the window. The server also enforces the same 7-day default
// for any request that arrives without date bounds (``_effective_window``
// in roundtable.py), so the default holds even for non-FE callers.
const DEFAULT_FILTERS = {
  timeRange: "weekly" as TimeRange,
  rangeMode: "rolling" as RangeMode,
  // PET-660 — direction defaults to Outbound (Inbound / Both still
  // selectable). Compared against this constant everywhere so a future
  // default flip can't leave a stale "1 active" filter badge.
  direction: "outbound" as RoundtableDirection,
  hasTranscript: true,
} as const;

type QaBlock = {
  id: string;
  question: string;
  answers: RoundtableAgentAnswer[];
  comparison: string;
  callRefs: CallRef[];
  // Built ONCE from this block's own call_refs when the block is
  // appended — phone ordinals are scoped to this question's analysed
  // pool, so the same call_id may carry different ordinals in different
  // blocks. Never collapse into one thread-wide map (searchQaCopy.ts
  // documents the same invariant for the Search QA thread).
  refMap: CallRefMap;
  // Scope the question was asked under, snapshotted at ask time so
  // mid-thread filter changes don't retroactively relabel old blocks.
  scopeLabel: string;
};

type Tab = "thread" | "matrix";

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------
// The answer-presentation helpers (answeredFromLabel,
// ROUNDTABLE_ERROR_COPY, agentErrorCopy, answerCaption, matrixColumns)
// moved to roundtableCopy.ts in stage 3 so the Copy Report payload and
// this render share one source of truth.

export function RoundtableView() {
  // PET-671 — managers may ask only the preset (suggested) questions an
  // admin configured for their team; the free-text box and Ask button are
  // hidden for them and a non-preset question is rejected server-side too.
  // Admins / supadmins are unrestricted. Role comes from the single
  // AuthContext (/auth/me), never a hardcoded string check elsewhere.
  const { user } = useAuth();
  const isManager = user?.role === "manager";

  // -- Panel + filters -----------------------------------------------------
  // The panel and its non-blocking, dismissable notice (partial team
  // adds / failed member fetch) live in ONE state object so a team-add
  // reconcile can compute BOTH atomically inside a pure functional
  // updater. The reconcile must read the LIVE panel — agent rows and
  // chip-remove buttons stay enabled while a team's member fetch is in
  // flight, so an agent added or removed mid-fetch must survive the
  // resolve — and the cap-overflow notice is derived from that same
  // live panel in the same update. Two separate useStates would force
  // either a stale-closure read (the original bug) or a side effect
  // inside the updater.
  const [panelState, setPanelState] = useState<{
    panel: AgentRow[];
    notice: string | null;
  }>({ panel: [], notice: null });
  const { panel, notice: panelNotice } = panelState;
  const [agentSearch, setAgentSearch] = useState("");
  // Id of the team whose members are currently being fetched/added.
  const [teamAdding, setTeamAdding] = useState<string | null>(null);
  const {
    timeRange,
    rangeMode,
    customRange,
    onTimeRangeChange,
    onRangeModeChange,
    onCustomRangeChange,
  } = useTimeRange({
    initial: {
      timeRange: DEFAULT_FILTERS.timeRange,
      rangeMode: DEFAULT_FILTERS.rangeMode,
    },
  });
  const [direction, setDirection] = useState<RoundtableDirection>(
    DEFAULT_FILTERS.direction,
  );
  const [hasTranscript, setHasTranscript] = useState<boolean>(
    DEFAULT_FILTERS.hasTranscript,
  );

  // -- Results -------------------------------------------------------------
  const [blocks, setBlocks] = useState<QaBlock[]>([]);
  const [question, setQuestion] = useState("");
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [qaLoading, setQaLoading] = useState(false);
  const [qaError, setQaError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("thread");
  const askRef = useRef<HTMLTextAreaElement | null>(null);
  // Copy Report feedback (SearchView's state machine): "copied" on
  // success, "failed" when the clipboard write didn't land (no
  // clipboard API in non-secure contexts, or a permission rejection).
  // Both reset to idle after 2s.
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const copiedTimerRef = useRef<number | null>(null);

  // Clear the copy-feedback timer on unmount so navigating away within
  // the 2s window can't fire a setState on an unmounted component.
  useEffect(() => {
    return () => {
      if (copiedTimerRef.current != null) {
        window.clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  // Authorised agents — the backend scopes this list to the caller's
  // visibility, so the picker can only ever offer agents they may query.
  const agentsQ = useQuery<AgentRow[]>({
    queryKey: ["roundtable-agents"],
    queryFn: () => api.agents(),
    refetchInterval: false,
  });
  const allAgents = useMemo(() => agentsQ.data ?? [], [agentsQ.data]);

  // Teams the caller can access — the same access-scoped list used
  // elsewhere, so the picker only ever offers teams they may query.
  const teamsQ = useQuery<MyTeam[]>({
    queryKey: ["roundtable-teams"],
    queryFn: () => api.myTeams(),
    refetchInterval: false,
  });

  // PET-641 — admin-curated preset questions, shared with the Search
  // Ask-a-Question box (same query key, so the admin editor's save
  // invalidation refreshes these bubbles too). A click populates the
  // textarea — never auto-sends.
  const presetsQ = useQuery<SuggestedQuestionsOut>({
    queryKey: ["suggested-questions"],
    queryFn: () => api.suggestedQuestions(),
  });
  const presets = presetsQ.data?.questions ?? [];

  const selectableAgents = useMemo(
    () => filterSelectableAgents(allAgents, panel, agentSearch),
    [allAgents, panel, agentSearch],
  );
  const matchableTeams = useMemo(
    () => filterMatchableTeams(teamsQ.data ?? [], agentSearch),
    [teamsQ.data, agentSearch],
  );

  // Refocus the ask box whenever an ask settles (success or error). The
  // textarea stays ENABLED while a question is in flight — a Roundtable
  // answer can take minutes, and the user must be able to line up the
  // next question meanwhile (Wave-3 focus/disabled lesson: disabling
  // the input also defeats a synchronous focus()).
  useEffect(() => {
    if (!qaLoading) askRef.current?.focus();
  }, [qaLoading]);

  function addAgent(row: AgentRow) {
    setPanelState((s) => ({ ...s, panel: addAgentToPanel(s.panel, row) }));
    setAgentSearch("");
  }

  function removeAgent(agentId: string | null) {
    setPanelState((s) => ({
      ...s,
      panel: s.panel.filter((a) => a.agent_id !== agentId),
    }));
  }

  // Fetch a team's members, reconcile each to the already-loaded agent
  // rows, and bulk-add (deduped, capped, out-of-scope members silently
  // skipped — see reconcileTeamAdd).
  async function addTeam(team: MyTeam) {
    if (panel.length >= MAX_PANEL || teamAdding) return;
    setTeamAdding(team.id);
    setPanelState((s) => ({ ...s, notice: null }));
    try {
      const members = await api.analyticsTeamAgents(team.id);
      // Reconcile inside the functional updater so it reads the LIVE
      // panel, not this closure's pre-await snapshot — agents added or
      // removed while the fetch was in flight survive the resolve, and
      // the overflow notice counts against that same live panel.
      setPanelState((s) => {
        const { nextPanel, notice } = reconcileTeamAdd(
          members,
          allAgents,
          s.panel,
          team.name,
        );
        return { panel: nextPanel, notice };
      });
      setAgentSearch("");
    } catch (e) {
      const msg =
        (e as ApiError)?.message ||
        (e as Error)?.message ||
        "Failed to add team members";
      setPanelState((s) => ({ ...s, notice: msg }));
    } finally {
      setTeamAdding(null);
    }
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const top = topSuggestion(matchableTeams, selectableAgents);
    if (!top) return;
    if (top.kind === "team") void addTeam(top.team);
    else addAgent(top.row);
  }

  async function ask(override?: string) {
    // PET-671 / Task #481 — a suggested-question bubble passes its text as
    // ``override`` so it submits the clicked value directly, never relying
    // on the async ``setQuestion`` having applied first. The typed-box and
    // Enter paths call ``ask()`` with no arg, falling back to ``question``.
    const q = (override ?? question).trim();
    if (!q || qaLoading || panel.length === 0) return;
    setQaLoading(true);
    setQaError(null);
    setPendingQuestion(q);
    // Clear the input immediately so the next question can be typed
    // while this one is in flight (the textarea is never disabled).
    setQuestion("");
    const scopeLabel = buildScopeLabel({
      timeRange,
      rangeMode,
      customRange,
      direction,
      hasTranscript,
    });
    try {
      const res = await api.roundtableQa(
        buildRoundtableQaIn({
          panel,
          question: q,
          timeRange,
          rangeMode,
          customRange,
          direction,
          hasTranscript,
        }),
      );
      const callRefs = res.call_refs ?? [];
      setBlocks((b) => [
        ...b,
        {
          id: `${Date.now()}-${b.length}`,
          question: res.question,
          answers: res.answers,
          comparison: res.comparison,
          callRefs,
          refMap: buildCallRefMap(callRefs),
          scopeLabel,
        },
      ]);
    } catch (e) {
      const msg =
        (e as ApiError)?.message ||
        (e as Error)?.message ||
        "Failed to get answers";
      setQaError(String(msg));
      // A failed ask puts the question back so it can be edited and
      // resent — but never clobbers anything typed since.
      setQuestion((cur) => (cur.trim().length > 0 ? cur : q));
    } finally {
      setPendingQuestion(null);
      setQaLoading(false);
    }
  }

  function clearResults() {
    setBlocks([]);
    setQaError(null);
    setQuestion("");
  }

  // Copy Report — exports the ACTIVE tab (Thread structure or the
  // Matrix table) as rich text + a plain-text fallback. The payload
  // builder routes all LLM text through the linkify helpers with each
  // block's own refMap (RAID #42: never escapeHtml of raw answers).
  async function copyReport() {
    if (blocks.length === 0) return;
    let ok = false;
    try {
      const { html, text } = buildRoundtableCopyPayload(
        blocks,
        panel.map(rowLabel),
        tab,
        buildScopeLabel({
          timeRange,
          rangeMode,
          customRange,
          direction,
          hasTranscript,
        }),
      );
      ok = await writeRichClipboard(html, text);
    } catch {
      // Clipboard can reject (permissions / focus loss). Fall through
      // to the "Copy failed" state — a silent failure would leave the
      // user thinking the report was on the clipboard.
    }
    setCopyState(ok ? "copied" : "failed");
    if (copiedTimerRef.current != null) {
      window.clearTimeout(copiedTimerRef.current);
    }
    copiedTimerRef.current = window.setTimeout(
      () => setCopyState("idle"),
      2000,
    );
  }

  function onAskKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void ask();
    }
  }

  const hasResults = blocks.length > 0;
  const panelFull = panel.length >= MAX_PANEL;
  const labelClass =
    "text-xs font-medium uppercase tracking-wide text-muted-foreground";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <MessagesSquare className="h-5 w-5 text-primary" aria-hidden="true" />
          Roundtable
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Compare a panel of agents side by side: scope their calls, then
          ask the same question across everyone. Results are session-only
          and aren't saved.
        </p>
      </div>

      {/* Panel builder */}
      <section className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-foreground">
            Panel{" "}
            <span className="font-normal text-muted-foreground">
              ({panel.length}/{MAX_PANEL})
            </span>
          </h2>
          {panel.length > 0 ? (
            // PET-671 / Task #482 — empty the whole panel in one click. All
            // roles; only shown when the panel has at least one agent.
            <button
              type="button"
              onClick={() => setPanelState((s) => ({ ...s, panel: [] }))}
              className="hover-elevate inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2.5 py-1 text-xs font-medium text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              Clear
            </button>
          ) : null}
        </div>

        {panel.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {panel.map((a) => (
              <span
                key={a.agent_id}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-medium text-primary"
              >
                {rowLabel(a)}
                <button
                  type="button"
                  onClick={() => removeAgent(a.agent_id)}
                  aria-label={`Remove ${rowLabel(a)}`}
                  className="hover-elevate rounded-full p-0.5"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground italic">
            Add at least one agent to begin.
          </p>
        )}

        <div className="mt-3 max-w-md">
          <input
            type="search"
            value={agentSearch}
            onChange={(e) => setAgentSearch(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={
              panelFull ? "Panel is full" : "Search agents or teams to add…"
            }
            aria-label="Search agents or teams to add"
            disabled={panelFull || agentsQ.isLoading}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          />
          {agentsQ.isLoading ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Loading agents…
            </p>
          ) : agentSearch.trim() && !panelFull ? (
            <ul className="mt-1 max-h-56 overflow-auto rounded-md border border-border bg-popover shadow-lg">
              {matchableTeams.length === 0 && selectableAgents.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">
                  No matching agents or teams
                </li>
              ) : (
                <>
                  {matchableTeams.length > 0 ? (
                    <>
                      <li className={`px-3 pt-2 pb-1 ${labelClass}`}>Teams</li>
                      {matchableTeams.map((t) => (
                        <li key={`team-${t.id}`}>
                          <button
                            type="button"
                            onClick={() => void addTeam(t)}
                            disabled={teamAdding !== null}
                            className="hover-elevate flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <Users
                                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                                aria-hidden="true"
                              />
                              <span className="truncate">{t.name}</span>
                              <span className="shrink-0 text-xs text-muted-foreground">
                                Add all members
                              </span>
                            </span>
                            {teamAdding === t.id ? (
                              <span className="shrink-0 text-xs text-muted-foreground">
                                Adding…
                              </span>
                            ) : (
                              <Plus
                                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        </li>
                      ))}
                    </>
                  ) : null}
                  {selectableAgents.length > 0 ? (
                    <>
                      {matchableTeams.length > 0 ? (
                        <li className={`px-3 pt-2 pb-1 ${labelClass}`}>
                          Agents
                        </li>
                      ) : null}
                      {selectableAgents.map((r) => (
                        <li key={r.agent_id}>
                          <button
                            type="button"
                            onClick={() => addAgent(r)}
                            className="hover-elevate flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <span className="truncate">{rowLabel(r)}</span>
                            <Plus
                              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                              aria-hidden="true"
                            />
                          </button>
                        </li>
                      ))}
                    </>
                  ) : null}
                </>
              )}
            </ul>
          ) : null}
          {panelNotice ? (
            <div className="mt-2 flex items-start justify-between gap-2 rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
              <span>{panelNotice}</span>
              <button
                type="button"
                onClick={() =>
                  setPanelState((s) => ({ ...s, notice: null }))
                }
                aria-label="Dismiss notice"
                className="hover-elevate shrink-0 rounded p-0.5"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {/* Call scope */}
      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">Call scope</h2>
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className={labelClass}>Date range</span>
            <TimeRangeControls
              value={timeRange}
              rangeMode={rangeMode}
              customRange={customRange}
              onValueChange={onTimeRangeChange}
              onRangeModeChange={onRangeModeChange}
              onCustomRangeChange={onCustomRangeChange}
            />
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1">
              <label className={labelClass} htmlFor="roundtable-direction">
                Direction
              </label>
              <select
                id="roundtable-direction"
                value={direction}
                onChange={(e) =>
                  setDirection(e.target.value as RoundtableDirection)
                }
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {DIRECTION_OPTIONS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <label className="inline-flex items-center gap-2 pb-1.5 text-sm text-foreground">
              <input
                type="checkbox"
                checked={hasTranscript}
                onChange={(e) => setHasTranscript(e.target.checked)}
                className="rounded border-input"
              />
              Only calls with a transcript
            </label>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {scopeCaption(timeRange, rangeMode, customRange)}
          {hasResults
            ? " Changes apply to your next question — earlier answers keep the scope they were asked under."
            : ""}
        </p>
      </section>

      {/* Results toolbar */}
      {hasResults ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div
            role="tablist"
            aria-label="Results layout"
            className="flex items-center gap-1 border-b border-border"
          >
            <TabButton
              active={tab === "thread"}
              onClick={() => setTab("thread")}
              label="Thread"
            />
            <TabButton
              active={tab === "matrix"}
              onClick={() => setTab("matrix")}
              label="Matrix"
            />
          </div>
          <div className="flex items-center gap-2">
            {/* The whole toolbar (and so this button) only exists once
                there are results — the SearchView hidden-before-first-
                answer pattern. Disabled while a question is in flight
                so the export can't race a block being appended. */}
            <button
              type="button"
              onClick={() => void copyReport()}
              disabled={qaLoading || !hasResults}
              className="hover-elevate inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              {copyState === "copied" ? (
                <Check className="h-4 w-4" aria-hidden="true" />
              ) : copyState === "failed" ? (
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Copy className="h-4 w-4" aria-hidden="true" />
              )}
              {copyState === "copied"
                ? "Copied"
                : copyState === "failed"
                  ? "Copy failed"
                  : "Copy Report"}
            </button>
            <button
              type="button"
              onClick={clearResults}
              className="hover-elevate inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Clear Results
            </button>
          </div>
        </div>
      ) : null}

      {/* Results body */}
      {hasResults ? (
        tab === "thread" ? (
          <ThreadBlocks blocks={blocks} />
        ) : (
          <MatrixBlocks blocks={blocks} />
        )
      ) : null}

      {/* Ask box */}
      {panel.length > 0 ? (
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold text-foreground">
            Ask the panel
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Each question is answered per agent and rolled up into a
            comparison takeaway. Big panels can take a few minutes per
            question.
          </p>
          {qaError ? (
            <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {qaError}
            </div>
          ) : null}
          {pendingQuestion ? (
            <div className="mt-3 space-y-1 text-sm">
              <div className="font-medium text-foreground">
                {pendingQuestion}
              </div>
              <div className="text-muted-foreground italic">
                Asking the panel — this can take a few minutes…
              </div>
            </div>
          ) : null}
          {presets.length > 0 ? (
            // PET-672 — bubbles grouped by colour; each group is one wrapped
            // row prefixed with a small colour dot. Already team-scoped
            // server-side (the chat-surface GET). PET-671 / Task #481 — a
            // click fills the box AND auto-submits, so a manager (who has no
            // Ask button) can run a preset in one tap. The typed-box paths
            // are unchanged.
            <div className="mt-3 space-y-2">
              {groupByColor(presets, (s) => s.color).map((group) => (
                <div
                  key={group.color ?? "none"}
                  className="flex flex-wrap items-center gap-1.5"
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${colorStyle(group.color).dot}`}
                    aria-hidden="true"
                  />
                  {group.items.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      title={s.text}
                      disabled={qaLoading}
                      onClick={() => {
                        setQuestion(s.text);
                        void ask(s.text);
                      }}
                      className={`max-w-[18rem] truncate rounded-full border px-3 py-1 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${colorStyle(s.color).bubble}`}
                    >
                      {s.text}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ) : isManager ? (
            // PET-671 — managers have no free-text box, so when an admin has
            // configured no presets visible to them there is nothing to ask.
            // Show a clear empty state rather than a hidden/broken control.
            <p className="mt-3 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              No questions available. Ask an admin to configure preset
              questions for your team.
            </p>
          ) : null}
          {isManager ? (
            // Managers ask only via the bubbles above; the free-text box and
            // Ask button are hidden. While a question is in flight they get a
            // dedicated processing cue (admins read the button's own state).
            qaLoading ? (
              <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Asking…
              </div>
            ) : null
          ) : (
            <div className="mt-3 flex items-end gap-2">
              <textarea
                ref={askRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={onAskKeyDown}
                rows={2}
                placeholder='e.g. "How did each agent handle pricing objections?"'
                aria-label="Ask the panel a question"
                className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <button
                type="button"
                onClick={() => void ask()}
                disabled={!question.trim() || qaLoading}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {qaLoading ? "Asking…" : "Ask"}
              </button>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}

// House tab pattern (SearchView / TrainingTipsModal): underline
// indicator via border-b-2, primary border + foreground text when
// active, muted otherwise.
function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        "rounded-t border-b-2 px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
        (active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground")
      }
    >
      {label}
    </button>
  );
}

// One agent's answer body, shared by the Thread cards and the Matrix
// cells: failed answers render their #205 category copy, zero-call
// agents render a clear empty state, and everything that came from the
// LLM goes through linkifyCallIds (per-block refMap).
function AgentAnswerBody({
  answer,
  keyPrefix,
  refMap,
}: {
  answer: RoundtableAgentAnswer;
  keyPrefix: string;
  refMap: CallRefMap;
}) {
  const errCopy = agentErrorCopy(answer);
  if (errCopy) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
        {errCopy}
      </div>
    );
  }
  if (answer.calls_used === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        {answer.answer || "No calls match the current filters for this agent."}
      </p>
    );
  }
  return (
    <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
      {linkifyCallIds(answer.answer ?? "", keyPrefix, refMap)}
    </p>
  );
}

function ThreadBlocks({ blocks }: { blocks: QaBlock[] }) {
  return (
    <div className="space-y-5">
      {blocks.map((block) => (
        <section
          key={block.id}
          className="rounded-lg border border-border bg-card p-4"
        >
          <h3 className="text-sm font-semibold text-foreground">
            {block.question}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Scope: {block.scopeLabel}
          </p>

          {/* Comparison takeaway leads each block — linkified like
              every other piece of LLM text. */}
          <div className="mt-3 rounded-md border border-primary/20 bg-primary/5 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-primary">
              Comparison takeaway
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {linkifyCallIds(
                block.comparison,
                `rt-cmp-${block.id}`,
                block.refMap,
              )}
            </p>
          </div>

          {/* Each agent's answer stacked vertically, full width. */}
          <div className="mt-3 space-y-3">
            {block.answers.map((a) => (
              <div
                key={a.agent_id}
                className="rounded-md border border-border bg-background p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {a.agent_name || a.agent_id}
                  </span>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {answerCaption(a)}
                  </span>
                </div>
                <div className="mt-2">
                  <AgentAnswerBody
                    answer={a}
                    keyPrefix={`rt-${block.id}-${a.agent_id}`}
                    refMap={block.refMap}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function MatrixBlocks({ blocks }: { blocks: QaBlock[] }) {
  const cols = matrixColumns(blocks);
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-muted/40">
            <th className="sticky left-0 z-10 min-w-[12rem] border-b border-r border-border bg-muted/40 p-3 text-left font-semibold text-foreground">
              Question
            </th>
            {cols.map((c) => (
              <th
                key={c.agentId}
                className="min-w-[16rem] border-b border-r border-border p-3 text-left font-semibold text-foreground"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {blocks.map((block) => (
            <Fragment key={block.id}>
              <tr className="align-top">
                <th className="sticky left-0 z-10 border-b border-r border-border bg-background p-3 text-left font-medium text-foreground">
                  {block.question}
                </th>
                {cols.map((c) => {
                  const ans = block.answers.find(
                    (x) => x.agent_id === c.agentId,
                  );
                  return (
                    <td
                      key={c.agentId}
                      className="border-b border-r border-border p-3 align-top"
                    >
                      {ans ? (
                        <>
                          <div className="mb-1 text-xs font-medium text-muted-foreground">
                            {answerCaption(ans)}
                          </div>
                          <AgentAnswerBody
                            answer={ans}
                            keyPrefix={`mx-${block.id}-${c.agentId}`}
                            refMap={block.refMap}
                          />
                        </>
                      ) : (
                        // Agent wasn't on the panel for this question.
                        <span className="text-muted-foreground italic">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
              {/* Full-width comparison row — linkified through the same
                  per-block refMap (the prototype rendered this raw,
                  leaking call IDs; fixed at port). */}
              <tr>
                <td
                  colSpan={cols.length + 1}
                  className="border-b border-border bg-primary/5 p-3"
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                    Comparison:{" "}
                  </span>
                  <span className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                    {linkifyCallIds(
                      block.comparison,
                      `mx-cmp-${block.id}`,
                      block.refMap,
                    )}
                  </span>
                </td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
